import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  PanResponder,
  Alert,
  Platform
} from 'react-native';
import {
  XStack,
  YStack,
  Button,
  Card,
  Separator,
  H4,
  H6
} from 'tamagui';
import {
  MaterialIcons,
  AntDesign,
  FontAwesome5,
  Ionicons
} from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import getId from '@api/getId';
import PropertiesPanel from './ComponentDesigner/PropertiesPanel';

// Types for component elements
interface ComponentElement {
  id: string;
  type: 'text' | 'header' | 'image' | 'input' | 'button' | 'container' | 'divider';
  x: number;
  y: number;
  width: number;
  height: number;
  properties: {
    text?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
    color?: string;
    backgroundColor?: string;
    borderRadius?: number;
    padding?: number;
    margin?: number;
    alignment?: 'left' | 'center' | 'right';
    dataBinding?: string;
    placeholder?: string;
    imageSource?: string;
    borderWidth?: number;
    borderColor?: string;
    height?: number; // For divider elements
  };
  style?: any;
}

interface CustomView {
  id: string;
  name: string;
  dataId: string;
  elements: ComponentElement[];
  canvasSettings: {
    width: number;
    height: number;
    backgroundColor: string;
    gridSize: number;
    showGrid: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

interface ComponentDesignerProps {
  dataId?: string;
  initialView?: CustomView;
  onSave?: (view: CustomView) => void;
  onCancel?: () => void;
  route?: any;
  navigation?: any;
}

const ComponentDesigner: React.FC<ComponentDesignerProps> = ({
  dataId: propDataId,
  initialView: propInitialView,
  onSave: propOnSave,
  onCancel: propOnCancel,
  route,
  navigation
}) => {
  // Extract params from route if available (when used as screen)
  const routeDataId = route?.params?.dataId;
  const routeDataTitle = route?.params?.dataTitle;
  const routeOnSave = route?.params?.onSave;

  // Use route params if available, otherwise use props
  const dataId = routeDataId || propDataId;
  const initialView = propInitialView;
  const onSave = routeOnSave || propOnSave;
  const onCancel = propOnCancel;

  const theme = themeSelector();
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  const queryClient = useQueryClient();

  // Fetch data schema for the data tab
  const { data: dataSchemaResponse, isLoading: isLoadingSchema, error: schemaError } = useQuery({
    queryKey: ['dataSchema', dataId],
    queryFn: async () => {
      if (!dataId) return null;
      const userId = await getId();
      const response = await apis.getDataSchema(userId, dataId);
      console.log('ComponentDesigner - Schema response:', response.data);
      return response.data;
    },
    enabled: !!dataId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract the actual schema from the response structure
  const dataSchema = dataSchemaResponse?.data?.dataSchema;
  console.log('ComponentDesigner - Extracted schema:', dataSchema);
  console.log('ComponentDesigner - Schema is array:', Array.isArray(dataSchema));

  // Canvas and viewport state
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(20);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [showRulers, setShowRulers] = useState(false);
  const [canvasBackground, setCanvasBackground] = useState(theme ? '#ffffff' : '#1a1a1a');

  // Component state
  const [elements, setElements] = useState<ComponentElement[]>(initialView?.elements || []);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [draggedElement, setDraggedElement] = useState<ComponentElement | null>(null);
  const [viewName, setViewName] = useState(initialView?.name || `Custom View for ${routeDataTitle || 'Data'}`);

  // UI state
  const [activeTab, setActiveTab] = useState<'elements' | 'properties' | 'data'>('elements');
  const [isPropertyPanelOpen, setIsPropertyPanelOpen] = useState(true);

  // Component library with categories
  const componentLibrary = [
    // Text Components
    {
      type: 'text',
      icon: 'text-fields',
      label: 'Text',
      color: '#2196F3',
      category: 'Text',
      description: 'Simple text display',
      defaultProps: {
        text: 'Sample Text',
        fontSize: 16,
        color: theme ? '#000000' : '#ffffff',
        fontWeight: 'normal' as const,
        alignment: 'left' as const
      }
    },
    {
      type: 'header',
      icon: 'title',
      label: 'Header',
      color: '#9C27B0',
      category: 'Text',
      description: 'Large heading text',
      defaultProps: {
        text: 'Header Text',
        fontSize: 24,
        color: theme ? '#000000' : '#ffffff',
        fontWeight: 'bold' as const,
        alignment: 'center' as const
      }
    },

    // Input Components
    {
      type: 'input',
      icon: 'input',
      label: 'Text Input',
      color: '#FF9800',
      category: 'Input',
      description: 'Single line text input',
      defaultProps: {
        placeholder: 'Enter text...',
        fontSize: 16,
        padding: 12,
        borderWidth: 1,
        borderColor: '#cccccc',
        borderRadius: 8,
        backgroundColor: theme ? '#ffffff' : '#333333'
      }
    },
    {
      type: 'button',
      icon: 'smart-button',
      label: 'Button',
      color: '#F44336',
      category: 'Input',
      description: 'Clickable action button',
      defaultProps: {
        text: 'Click Me',
        fontSize: 16,
        fontWeight: 'bold' as const,
        color: '#ffffff',
        backgroundColor: '#2196F3',
        padding: 12,
        borderRadius: 8
      }
    },

    // Media Components
    {
      type: 'image',
      icon: 'image',
      label: 'Image',
      color: '#4CAF50',
      category: 'Media',
      description: 'Image placeholder',
      defaultProps: {
        borderRadius: 8,
        imageSource: 'https://via.placeholder.com/100'
      }
    },

    // Layout Components
    {
      type: 'container',
      icon: 'crop-landscape',
      label: 'Container',
      color: '#607D8B',
      category: 'Layout',
      description: 'Flexible container box',
      defaultProps: {
        backgroundColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
        borderWidth: 1,
        borderColor: theme ? '#e0e0e0' : '#444444',
        borderRadius: 8,
        padding: 16
      }
    },
    {
      type: 'divider',
      icon: 'horizontal-rule',
      label: 'Divider',
      color: '#795548',
      category: 'Layout',
      description: 'Horizontal separator line',
      defaultProps: {
        height: 2,
        backgroundColor: theme ? '#e0e0e0' : '#444444',
        margin: 8
      }
    }
  ];

  // Group components by category
  const groupedComponents = componentLibrary.reduce((acc, component) => {
    const category = component.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(component);
    return acc;
  }, {} as Record<string, typeof componentLibrary>);

  // Generate unique ID for elements
  const generateId = () => `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Handle drag and drop from component library
  const handleLibraryDragStart = useCallback((componentType: string) => {
    const libraryComponent = componentLibrary.find(comp => comp.type === componentType);
    if (!libraryComponent) return;

    // Calculate initial position based on canvas center and existing elements
    const baseX = 50;
    const baseY = 50;
    const offset = elements.length * 20; // Offset for multiple elements

    const newElement: ComponentElement = {
      id: generateId(),
      type: componentType as any,
      x: baseX + offset,
      y: baseY + offset,
      width: getDefaultWidth(componentType),
      height: getDefaultHeight(componentType),
      properties: { ...libraryComponent.defaultProps },
      style: {}
    };

    setElements(prev => [...prev, newElement]);
    setSelectedElement(newElement.id);

    // Add visual feedback
    Alert.alert(
      'Element Added',
      `${libraryComponent.label} has been added to the canvas`,
      [{ text: 'OK', style: 'default' }],
      { cancelable: true }
    );
  }, [componentLibrary, elements.length]);

  // Get default dimensions for different component types
  const getDefaultWidth = (type: string) => {
    switch (type) {
      case 'text': return 120;
      case 'header': return 200;
      case 'image': return 100;
      case 'input': return 200;
      case 'button': return 120;
      case 'container': return 250;
      case 'divider': return 200;
      default: return 120;
    }
  };

  const getDefaultHeight = (type: string) => {
    switch (type) {
      case 'text': return 30;
      case 'header': return 40;
      case 'image': return 100;
      case 'input': return 45;
      case 'button': return 45;
      case 'container': return 150;
      case 'divider': return 2;
      default: return 30;
    }
  };

  // Handle element selection
  const handleElementSelect = useCallback((elementId: string) => {
    setSelectedElement(elementId);
  }, []);

  // Handle element position update with snap-to-grid
  const handleElementMove = useCallback((elementId: string, newX: number, newY: number) => {
    let finalX = newX;
    let finalY = newY;

    // Apply snap-to-grid if enabled
    if (snapToGrid) {
      finalX = Math.round(newX / gridSize) * gridSize;
      finalY = Math.round(newY / gridSize) * gridSize;
    }

    // Keep element within canvas bounds
    finalX = Math.max(0, Math.min(finalX, canvasSize.width - 50));
    finalY = Math.max(0, Math.min(finalY, canvasSize.height - 30));

    setElements(prev => prev.map(el =>
      el.id === elementId
        ? { ...el, x: finalX, y: finalY }
        : el
    ));
  }, [snapToGrid, gridSize, canvasSize]);

  // Handle element resize
  const handleElementResize = useCallback((elementId: string, newWidth: number, newHeight: number) => {
    setElements(prev => prev.map(el =>
      el.id === elementId
        ? { ...el, width: newWidth, height: newHeight }
        : el
    ));
  }, []);

  // Handle element property update
  const handlePropertyUpdate = useCallback((elementId: string, property: string, value: any) => {
    setElements(prev => prev.map(el =>
      el.id === elementId
        ? {
          ...el,
          properties: {
            ...el.properties,
            [property]: value
          }
        }
        : el
    ));
  }, []);

  // Handle element deletion
  const handleElementDelete = useCallback((elementId: string) => {
    setElements(prev => prev.filter(el => el.id !== elementId));
    if (selectedElement === elementId) {
      setSelectedElement(null);
    }
  }, [selectedElement]);

  // Canvas zoom and pan handlers
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.2, 0.3));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  // Element alignment functions
  const alignSelectedElements = useCallback((alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    const selectedElementsList = elements.filter(el => selectedElement === el.id || selectedElement?.includes(el.id));
    if (selectedElementsList.length === 0) return;

    const updatedElements = elements.map(el => {
      if (selectedElement === el.id || selectedElement?.includes(el.id)) {
        switch (alignment) {
          case 'left':
            return { ...el, x: 0 };
          case 'center':
            return { ...el, x: (canvasSize.width - el.width) / 2 };
          case 'right':
            return { ...el, x: canvasSize.width - el.width };
          case 'top':
            return { ...el, y: 0 };
          case 'middle':
            return { ...el, y: (canvasSize.height - el.height) / 2 };
          case 'bottom':
            return { ...el, y: canvasSize.height - el.height };
          default:
            return el;
        }
      }
      return el;
    });

    setElements(updatedElements);
  }, [elements, selectedElement, canvasSize]);

  // Distribute elements evenly
  const distributeElements = useCallback((direction: 'horizontal' | 'vertical') => {
    const selectedElementsList = elements.filter(el => selectedElement === el.id || selectedElement?.includes(el.id));
    if (selectedElementsList.length < 3) return;

    const sortedElements = [...selectedElementsList].sort((a, b) =>
      direction === 'horizontal' ? a.x - b.x : a.y - b.y
    );

    const first = sortedElements[0];
    const last = sortedElements[sortedElements.length - 1];
    const totalSpace = direction === 'horizontal'
      ? last.x - first.x
      : last.y - first.y;
    const spacing = totalSpace / (sortedElements.length - 1);

    const updatedElements = elements.map(el => {
      const index = sortedElements.findIndex(sorted => sorted.id === el.id);
      if (index > 0 && index < sortedElements.length - 1) {
        const newPosition = direction === 'horizontal'
          ? { ...el, x: first.x + spacing * index }
          : { ...el, y: first.y + spacing * index };
        return newPosition;
      }
      return el;
    });

    setElements(updatedElements);
  }, [elements, selectedElement]);

  // Grid size adjustment
  const adjustGridSize = useCallback((increment: boolean) => {
    const newSize = increment
      ? Math.min(gridSize + 5, 50)
      : Math.max(gridSize - 5, 5);
    setGridSize(newSize);
  }, [gridSize]);

  // Save custom view
  const saveMutation = useMutation({
    mutationFn: async (viewData: Partial<CustomView>) => {
      if (!dataId) {
        throw new Error('dataId is required to save custom view');
      }
      return await apis.saveCustomView({
        ...viewData,
        dataId
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['customViews', dataId] });
      Alert.alert('Success', 'Custom view saved successfully!');
      onSave?.(response.data);

      // If we have navigation (used as screen), go back
      if (navigation) {
        navigation.goBack();
      }
    },
    onError: (error) => {
      Alert.alert('Error', 'Failed to save custom view');
      console.error('Save error:', error);
    }
  });

  const handleSave = useCallback(() => {
    if (!viewName.trim()) {
      Alert.alert('Error', 'Please enter a view name');
      return;
    }

    const canvasSettings = {
      width: canvasSize.width,
      height: canvasSize.height,
      backgroundColor: theme ? '#ffffff' : '#1a1a1a',
      gridSize,
      showGrid
    };

    saveMutation.mutate({
      id: initialView?.id,
      name: viewName,
      dataId,
      elements,
      canvasSettings
    });
  }, [viewName, dataId, elements, canvasSize, gridSize, showGrid, theme, saveMutation, initialView]);

  return (
    <View style={{
      flex: 1,
      backgroundColor: theme ? '#f5f5f5' : '#121212'
    }}>
      {/* Header Toolbar */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 12,
        backgroundColor: theme ? '#ffffff' : '#1e1e1e',
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e0e0e0' : '#333333',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: theme ? 0.1 : 0.3,
        shadowRadius: 4,
        elevation: 3,
      }}>
        {/* Left side - Back button and View info */}
        <XStack alignItems="center" gap="$3">
          {/* Back button when used as screen */}
          {navigation && (
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={{
                padding: 8,
                borderRadius: 6,
                backgroundColor: theme ? '#f0f0f0' : '#333333',
                marginRight: 8
              }}
            >
              <MaterialIcons
                name="arrow-back"
                size={20}
                color={theme ? '#333333' : '#ffffff'}
              />
            </TouchableOpacity>
          )}

          <MaterialIcons
            name="design-services"
            size={24}
            color={theme ? '#333333' : '#ffffff'}
          />
          <View>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: theme ? '#333333' : '#ffffff'
            }}>
              Component Designer
            </Text>
            <Text style={{
              fontSize: 12,
              color: theme ? '#666666' : '#cccccc'
            }}>
              {viewName} • {elements.length} elements
            </Text>
          </View>
        </XStack>

        {/* Center - Canvas controls */}
        <XStack alignItems="center" gap="$2">
          <TouchableOpacity
            onPress={handleZoomOut}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: theme ? '#f0f0f0' : '#333333'
            }}
          >
            <MaterialIcons
              name="zoom-out"
              size={20}
              color={theme ? '#333333' : '#ffffff'}
            />
          </TouchableOpacity>

          <Text style={{
            minWidth: 60,
            textAlign: 'center',
            fontSize: 14,
            fontWeight: '600',
            color: theme ? '#333333' : '#ffffff'
          }}>
            {Math.round(zoom * 100)}%
          </Text>

          <TouchableOpacity
            onPress={handleZoomIn}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: theme ? '#f0f0f0' : '#333333'
            }}
          >
            <MaterialIcons
              name="zoom-in"
              size={20}
              color={theme ? '#333333' : '#ffffff'}
            />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={handleZoomReset}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: theme ? '#f0f0f0' : '#333333'
            }}
          >
            <MaterialIcons
              name="center-focus-strong"
              size={20}
              color={theme ? '#333333' : '#ffffff'}
            />
          </TouchableOpacity>

          {/* Separator */}
          <View style={{
            width: 1,
            height: 24,
            backgroundColor: theme ? '#e0e0e0' : '#444444',
            marginHorizontal: 4
          }} />

          <TouchableOpacity
            onPress={() => setShowGrid(!showGrid)}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: showGrid
                ? (theme ? '#e3f2fd' : '#1a237e')
                : (theme ? '#f0f0f0' : '#333333')
            }}
          >
            <MaterialIcons
              name="grid-on"
              size={20}
              color={showGrid
                ? '#2196F3'
                : (theme ? '#333333' : '#ffffff')
              }
            />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setSnapToGrid(!snapToGrid)}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: snapToGrid
                ? (theme ? '#e8f5e8' : '#1b5e20')
                : (theme ? '#f0f0f0' : '#333333')
            }}
          >
            <MaterialIcons
              name="grid-4x4"
              size={20}
              color={snapToGrid
                ? '#4CAF50'
                : (theme ? '#333333' : '#ffffff')
              }
            />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setShowRulers(!showRulers)}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: showRulers
                ? (theme ? '#fff3e0' : '#e65100')
                : (theme ? '#f0f0f0' : '#333333')
            }}
          >
            <MaterialIcons
              name="straighten"
              size={20}
              color={showRulers
                ? '#FF9800'
                : (theme ? '#333333' : '#ffffff')
              }
            />
          </TouchableOpacity>

          {/* Canvas size indicator */}
          <View style={{
            paddingHorizontal: 8,
            paddingVertical: 4,
            backgroundColor: theme ? '#f5f5f5' : '#2a2a2a',
            borderRadius: 4,
            marginLeft: 8
          }}>
            <Text style={{
              fontSize: 11,
              color: theme ? '#666666' : '#cccccc',
              fontWeight: '600'
            }}>
              {canvasSize.width} × {canvasSize.height}
            </Text>
          </View>
        </XStack>

        {/* Right side - Actions */}
        <XStack alignItems="center" gap="$2">
          <TouchableOpacity
            onPress={onCancel}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 6,
              backgroundColor: theme ? '#f0f0f0' : '#333333'
            }}
          >
            <Text style={{
              fontSize: 14,
              fontWeight: '600',
              color: theme ? '#333333' : '#ffffff'
            }}>
              Cancel
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={handleSave}
            disabled={saveMutation.isPending}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 6,
              backgroundColor: '#2196F3',
              opacity: saveMutation.isPending ? 0.7 : 1
            }}
          >
            <Text style={{
              fontSize: 14,
              fontWeight: '600',
              color: '#ffffff'
            }}>
              {saveMutation.isPending ? 'Saving...' : 'Save View'}
            </Text>
          </TouchableOpacity>
        </XStack>
      </View>

      {/* Main Content Area */}
      <View style={{ flex: 1, flexDirection: 'row' }}>
        {/* Component Library Sidebar */}
        <View style={{
          width: 280,
          backgroundColor: theme ? '#ffffff' : '#1e1e1e',
          borderRightWidth: 1,
          borderRightColor: theme ? '#e0e0e0' : '#333333'
        }}>
          {/* Sidebar Header */}
          <View style={{
            padding: 16,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e0e0e0' : '#333333'
          }}>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: theme ? '#333333' : '#ffffff',
              marginBottom: 8
            }}>
              Components
            </Text>

            {/* Tab Navigation */}
            <XStack gap="$1">
              {[
                { key: 'elements', label: 'Elements', icon: 'widgets' },
                { key: 'properties', label: 'Properties', icon: 'tune' },
                { key: 'data', label: 'Data', icon: 'storage' }
              ].map((tab) => (
                <TouchableOpacity
                  key={tab.key}
                  onPress={() => setActiveTab(tab.key as any)}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 6,
                    backgroundColor: activeTab === tab.key
                      ? (theme ? '#e3f2fd' : '#1a237e')
                      : 'transparent'
                  }}
                >
                  <MaterialIcons
                    name={tab.icon as any}
                    size={16}
                    color={activeTab === tab.key
                      ? '#2196F3'
                      : (theme ? '#666666' : '#cccccc')
                    }
                    style={{ marginRight: 4 }}
                  />
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: activeTab === tab.key
                      ? '#2196F3'
                      : (theme ? '#666666' : '#cccccc')
                  }}>
                    {tab.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </XStack>
          </View>

          {/* Sidebar Content */}
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {activeTab === 'elements' && (
              <YStack gap="$3">
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#666666' : '#cccccc',
                  marginBottom: 8
                }}>
                  Drag components to canvas
                </Text>

                {Object.entries(groupedComponents).map(([category, components]) => (
                  <View key={category}>
                    {/* Category Header */}
                    <Text style={{
                      fontSize: 13,
                      fontWeight: 'bold',
                      color: theme ? '#333333' : '#ffffff',
                      marginBottom: 8,
                      marginTop: category !== Object.keys(groupedComponents)[0] ? 16 : 0
                    }}>
                      {category}
                    </Text>

                    {/* Category Components */}
                    <YStack gap="$2">
                      {components.map((component) => (
                        <Animatable.View
                          key={component.type}
                          animation="fadeInUp"
                          duration={300}
                          delay={components.indexOf(component) * 50}
                        >
                          <TouchableOpacity
                            onPress={() => handleLibraryDragStart(component.type)}
                            activeOpacity={0.8}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              padding: 12,
                              borderRadius: 8,
                              backgroundColor: theme ? '#f8f9fa' : '#2a2a2a',
                              borderWidth: 1,
                              borderColor: theme ? '#e9ecef' : '#404040',
                              marginBottom: 6,
                              shadowColor: '#000',
                              shadowOffset: { width: 0, height: 1 },
                              shadowOpacity: theme ? 0.1 : 0.3,
                              shadowRadius: 2,
                              elevation: 2
                            }}
                          >
                            <View style={{
                              width: 36,
                              height: 36,
                              borderRadius: 6,
                              backgroundColor: component.color + '20',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: 12
                            }}>
                              <MaterialIcons
                                name={component.icon as any}
                                size={20}
                                color={component.color}
                              />
                            </View>

                            <View style={{ flex: 1 }}>
                              <Text style={{
                                fontSize: 14,
                                fontWeight: '600',
                                color: theme ? '#333333' : '#ffffff',
                                marginBottom: 2,
                                userSelect: 'none'
                              }}>
                                {component.label}
                              </Text>
                              <Text style={{
                                fontSize: 11,
                                color: theme ? '#666666' : '#cccccc',
                                userSelect: 'none'
                              }}>
                                {component.description}
                              </Text>
                            </View>

                            <View style={{
                              width: 28,
                              height: 28,
                              borderRadius: 14,
                              backgroundColor: component.color + '15',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <MaterialIcons
                                name="add"
                                size={16}
                                color={component.color}
                              />
                            </View>
                          </TouchableOpacity>
                        </Animatable.View>
                      ))}
                    </YStack>
                  </View>
                ))}

                {/* Quick Stats */}
                <View style={{
                  marginTop: 16,
                  padding: 12,
                  backgroundColor: theme ? '#f0f8ff' : '#1a237e',
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: theme ? '#e3f2fd' : '#3949ab'
                }}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: '#2196F3',
                    textAlign: 'center'
                  }}>
                    {componentLibrary.length} Components Available
                  </Text>
                  <Text style={{
                    fontSize: 10,
                    color: theme ? '#666666' : '#cccccc',
                    textAlign: 'center',
                    marginTop: 2
                  }}>
                    Tap any component to add it to the canvas
                  </Text>
                </View>
              </YStack>
            )}

            {activeTab === 'properties' && (
              <PropertiesPanel
                selectedElement={elements.find(el => el.id === selectedElement) || null}
                onPropertyUpdate={(property, value) => selectedElement && handlePropertyUpdate(selectedElement, property, value)}
                onPositionUpdate={(x, y) => selectedElement && handleElementMove(selectedElement, x, y)}
                onSizeUpdate={(w, h) => selectedElement && handleElementResize(selectedElement, w, h)}
                theme={theme}
              />
            )}

            {activeTab === 'data' && (
              <YStack gap="$3">
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#333333' : '#ffffff',
                  marginBottom: 8
                }}>
                  Data Schema - {routeDataTitle || 'Data Fields'}
                </Text>

                {isLoadingSchema && (
                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#666666' : '#cccccc'
                  }}>
                    Loading schema...
                  </Text>
                )}

                {schemaError && (
                  <Text style={{
                    fontSize: 12,
                    color: '#ff4444'
                  }}>
                    Error loading schema: {schemaError.message}
                  </Text>
                )}

                {dataSchema && !isLoadingSchema && (
                  <ScrollView style={{ maxHeight: 400 }}>
                    {Array.isArray(dataSchema) ? dataSchema.map((fieldInfo, index) => (
                      <TouchableOpacity
                        key={fieldInfo.title || index}
                        style={{
                          padding: 12,
                          marginVertical: 4,
                          backgroundColor: theme ? '#f8f9fa' : '#2a2a2a',
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: theme ? '#e0e0e0' : '#333333'
                        }}
                        onPress={() => {
                          // Copy field name to clipboard for easy binding
                          Alert.alert('Field Name', `${fieldInfo.title}\n\nTap to copy field name for data binding.`, [
                            { text: 'Cancel', style: 'cancel' },
                            {
                              text: 'Copy',
                              onPress: () => {
                                // Note: In a real implementation, you'd use Clipboard API
                                Alert.alert('Copied', `Field name "${fieldInfo.title}" copied to clipboard`);
                              }
                            }
                          ]);
                        }}
                      >
                        <Text style={{
                          fontSize: 13,
                          fontWeight: '600',
                          color: theme ? '#333333' : '#ffffff',
                          marginBottom: 4
                        }}>
                          {fieldInfo.title}
                        </Text>
                        <Text style={{
                          fontSize: 11,
                          color: theme ? '#666666' : '#cccccc'
                        }}>
                          Type: {fieldInfo.type || 'Unknown'}
                          {fieldInfo.validation && ' • Validated'}
                        </Text>
                        {fieldInfo.value && (
                          <Text style={{
                            fontSize: 10,
                            color: theme ? '#888888' : '#aaaaaa',
                            marginTop: 2
                          }}>
                            Example: {fieldInfo.value}
                          </Text>
                        )}
                        {fieldInfo.validation?.ref && (
                          <Text style={{
                            fontSize: 10,
                            color: theme ? '#007acc' : '#66b3ff',
                            marginTop: 2
                          }}>
                            Validation: {fieldInfo.validation.ref}
                          </Text>
                        )}
                      </TouchableOpacity>
                    )) : Object.entries(dataSchema || {}).map(([fieldName, fieldInfo]) => (
                      <TouchableOpacity
                        key={fieldName}
                        style={{
                          padding: 12,
                          marginVertical: 4,
                          backgroundColor: theme ? '#f8f9fa' : '#2a2a2a',
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: theme ? '#e0e0e0' : '#333333'
                        }}
                        onPress={() => {
                          // Copy field name to clipboard for easy binding
                          Alert.alert('Field Name', `${fieldName}\n\nTap to copy field name for data binding.`, [
                            { text: 'Cancel', style: 'cancel' },
                            {
                              text: 'Copy',
                              onPress: () => {
                                // Note: In a real implementation, you'd use Clipboard API
                                Alert.alert('Copied', `Field name "${fieldName}" copied to clipboard`);
                              }
                            }
                          ]);
                        }}
                      >
                        <Text style={{
                          fontSize: 13,
                          fontWeight: '600',
                          color: theme ? '#333333' : '#ffffff',
                          marginBottom: 4
                        }}>
                          {fieldName}
                        </Text>
                        <Text style={{
                          fontSize: 11,
                          color: theme ? '#666666' : '#cccccc'
                        }}>
                          Type: {(fieldInfo as any)?.type || 'Unknown'}
                          {(fieldInfo as any)?.required && ' • Required'}
                          {(fieldInfo as any)?.validation && ' • Validated'}
                        </Text>
                        {(fieldInfo as any)?.title && (
                          <Text style={{
                            fontSize: 10,
                            color: theme ? '#888888' : '#aaaaaa',
                            marginTop: 2
                          }}>
                            {(fieldInfo as any).title}
                          </Text>
                        )}
                      </TouchableOpacity>
                    ))}

                    {(!dataSchema || (Array.isArray(dataSchema) ? dataSchema.length === 0 : Object.keys(dataSchema).length === 0)) && (
                      <Text style={{
                        fontSize: 12,
                        color: theme ? '#666666' : '#cccccc',
                        textAlign: 'center',
                        fontStyle: 'italic',
                        padding: 20
                      }}>
                        No schema fields found for this data model
                      </Text>
                    )}
                  </ScrollView>
                )}

                {!dataId && (
                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#666666' : '#cccccc',
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}>
                    No data ID provided
                  </Text>
                )}
              </YStack>
            )}
          </ScrollView>
        </View>

        {/* Canvas Area */}
        <View style={{ flex: 1 }}>
          {/* Rulers */}
          {showRulers && (
            <>
              {/* Horizontal ruler */}
              <View style={{
                height: 20,
                backgroundColor: theme ? '#f8f9fa' : '#2a2a2a',
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e0e0e0' : '#333333',
                flexDirection: 'row'
              }}>
                {Array.from({ length: Math.ceil(canvasSize.width / 50) }).map((_, i) => (
                  <View key={i} style={{
                    width: 50 * zoom,
                    height: '100%',
                    borderRightWidth: 1,
                    borderRightColor: theme ? '#e0e0e0' : '#333333',
                    justifyContent: 'flex-end',
                    paddingBottom: 2
                  }}>
                    <Text style={{
                      fontSize: 9,
                      color: theme ? '#666666' : '#cccccc',
                      textAlign: 'center'
                    }}>
                      {i * 50}
                    </Text>
                  </View>
                ))}
              </View>

              {/* Vertical ruler */}
              <View style={{
                position: 'absolute',
                left: 0,
                top: showRulers ? 20 : 0,
                width: 20,
                height: '100%',
                backgroundColor: theme ? '#f8f9fa' : '#2a2a2a',
                borderRightWidth: 1,
                borderRightColor: theme ? '#e0e0e0' : '#333333',
                zIndex: 10
              }}>
                {Array.from({ length: Math.ceil(canvasSize.height / 50) }).map((_, i) => (
                  <View key={i} style={{
                    height: 50 * zoom,
                    width: '100%',
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? '#e0e0e0' : '#333333',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Text style={{
                      fontSize: 9,
                      color: theme ? '#666666' : '#cccccc',
                      transform: [{ rotate: '-90deg' }]
                    }}>
                      {i * 50}
                    </Text>
                  </View>
                ))}
              </View>
            </>
          )}

          <ScrollView
            style={{
              flex: 1,
              marginLeft: showRulers ? 20 : 0,
              marginTop: showRulers ? 0 : 0
            }}
            contentContainerStyle={{
              minWidth: canvasSize.width * zoom + 100,
              minHeight: canvasSize.height * zoom + 100,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme ? '#f0f0f0' : '#0f0f0f',
              paddingVertical: 50,
              paddingHorizontal: 50
            }}
            showsHorizontalScrollIndicator={true}
            showsVerticalScrollIndicator={true}
            minimumZoomScale={0.3}
            maximumZoomScale={3}
            zoomScale={zoom}
          >
            {/* Canvas */}
            <View
              style={{
                width: canvasSize.width,
                height: canvasSize.height,
                backgroundColor: canvasBackground,
                borderWidth: 2,
                borderColor: theme ? '#e0e0e0' : '#333333',
                borderRadius: 8,
                position: 'relative',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: theme ? 0.1 : 0.3,
                shadowRadius: 8,
                elevation: 5,
                transform: [{ scale: zoom }]
              }}
            >
              {/* Grid overlay */}
              {showGrid && (
                <View style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.4
                }}>
                  {/* Major grid lines (every 5 units) */}
                  {Array.from({ length: Math.ceil(canvasSize.width / gridSize) }).map((_, i) => (
                    <View
                      key={`v-major-${i}`}
                      style={{
                        position: 'absolute',
                        left: i * gridSize,
                        top: 0,
                        bottom: 0,
                        width: i % 5 === 0 ? 1 : 0.5,
                        backgroundColor: i % 5 === 0
                          ? (theme ? '#2196F3' : '#64B5F6')
                          : (theme ? '#e0e0e0' : '#333333')
                      }}
                    />
                  ))}
                  {Array.from({ length: Math.ceil(canvasSize.height / gridSize) }).map((_, i) => (
                    <View
                      key={`h-major-${i}`}
                      style={{
                        position: 'absolute',
                        top: i * gridSize,
                        left: 0,
                        right: 0,
                        height: i % 5 === 0 ? 1 : 0.5,
                        backgroundColor: i % 5 === 0
                          ? (theme ? '#2196F3' : '#64B5F6')
                          : (theme ? '#e0e0e0' : '#333333')
                      }}
                    />
                  ))}

                  {/* Grid size indicator */}
                  <View style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    backgroundColor: 'rgba(33, 150, 243, 0.8)',
                    borderRadius: 4
                  }}>
                    <Text style={{
                      fontSize: 10,
                      color: '#ffffff',
                      fontWeight: '600'
                    }}>
                      Grid: {gridSize}px
                    </Text>
                  </View>
                </View>
              )}

              {/* Render elements */}
              {elements.map((element) => (
                <CanvasElement
                  key={element.id}
                  element={element}
                  isSelected={selectedElement === element.id}
                  onSelect={() => handleElementSelect(element.id)}
                  onMove={(x, y) => handleElementMove(element.id, x, y)}
                  onResize={(w, h) => handleElementResize(element.id, w, h)}
                  onDelete={() => handleElementDelete(element.id)}
                  theme={theme}
                />
              ))}

              {/* Canvas center indicator */}
              <View style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 20,
                height: 20,
                borderRadius: 10,
                backgroundColor: 'rgba(33, 150, 243, 0.3)',
                borderWidth: 2,
                borderColor: '#2196F3',
                transform: [{ translateX: -10 }, { translateY: -10 }]
              }} />

              {/* Canvas info overlay */}
              <View style={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                flexDirection: 'row',
                gap: 8
              }}>
                <View style={{
                  paddingHorizontal: 6,
                  paddingVertical: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: 4
                }}>
                  <Text style={{
                    fontSize: 10,
                    color: '#ffffff',
                    fontWeight: '600'
                  }}>
                    {elements.length} elements
                  </Text>
                </View>

                {snapToGrid && (
                  <View style={{
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    backgroundColor: 'rgba(76, 175, 80, 0.8)',
                    borderRadius: 4
                  }}>
                    <Text style={{
                      fontSize: 10,
                      color: '#ffffff',
                      fontWeight: '600'
                    }}>
                      Snap: ON
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

// Canvas Element Component
interface CanvasElementProps {
  element: ComponentElement;
  isSelected: boolean;
  onSelect: () => void;
  onMove: (x: number, y: number) => void;
  onResize: (width: number, height: number) => void;
  onDelete: () => void;
  theme: boolean;
}

const CanvasElement: React.FC<CanvasElementProps> = ({
  element,
  isSelected,
  onSelect,
  onMove,
  onResize,
  onDelete,
  theme
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const panResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: (evt) => {
      setIsDragging(true);
      onSelect();

      // Calculate offset from touch point to element origin
      const touchX = evt.nativeEvent.locationX;
      const touchY = evt.nativeEvent.locationY;
      setDragOffset({ x: touchX, y: touchY });
    },
    onPanResponderMove: (evt, gestureState) => {
      // Apply drag offset for more natural dragging
      const newX = Math.max(0, element.x + gestureState.dx);
      const newY = Math.max(0, element.y + gestureState.dy);
      onMove(newX, newY);
    },
    onPanResponderRelease: () => {
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
    }
  });

  const renderElementContent = () => {
    const commonStyle = {
      width: '100%' as const,
      height: '100%' as const
    };

    switch (element.type) {
      case 'text':
        return (
          <View style={[commonStyle, {
            justifyContent: 'center',
            alignItems: element.properties.alignment === 'center' ? 'center' :
              element.properties.alignment === 'right' ? 'flex-end' : 'flex-start'
          }]}>
            <Text style={{
              fontSize: element.properties.fontSize || 16,
              fontWeight: element.properties.fontWeight || 'normal',
              color: element.properties.color || (theme ? '#000000' : '#ffffff'),
              textAlign: element.properties.alignment || 'left'
            }}>
              {element.properties.text || 'Text'}
            </Text>
          </View>
        );

      case 'header':
        return (
          <View style={[commonStyle, {
            justifyContent: 'center',
            alignItems: element.properties.alignment === 'center' ? 'center' :
              element.properties.alignment === 'right' ? 'flex-end' : 'flex-start'
          }]}>
            <Text style={{
              fontSize: element.properties.fontSize || 24,
              fontWeight: element.properties.fontWeight || 'bold',
              color: element.properties.color || (theme ? '#000000' : '#ffffff'),
              textAlign: element.properties.alignment || 'center'
            }}>
              {element.properties.text || 'Header'}
            </Text>
          </View>
        );

      case 'input':
        return (
          <View style={[commonStyle, {
            padding: element.properties.padding || 12,
            borderWidth: element.properties.borderWidth || 1,
            borderColor: element.properties.borderColor || '#cccccc',
            borderRadius: element.properties.borderRadius || 8,
            backgroundColor: element.properties.backgroundColor || (theme ? '#ffffff' : '#333333'),
            justifyContent: 'center'
          }]}>
            <Text style={{
              fontSize: element.properties.fontSize || 16,
              color: theme ? '#999999' : '#cccccc'
            }}>
              {element.properties.placeholder || 'Input field'}
            </Text>
          </View>
        );

      case 'button':
        return (
          <View style={[commonStyle, {
            padding: element.properties.padding || 12,
            backgroundColor: element.properties.backgroundColor || '#2196F3',
            borderRadius: element.properties.borderRadius || 8,
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.2,
            shadowRadius: 4,
            elevation: 3
          }]}>
            <Text style={{
              fontSize: element.properties.fontSize || 16,
              fontWeight: element.properties.fontWeight || 'bold',
              color: element.properties.color || '#ffffff'
            }}>
              {element.properties.text || 'Button'}
            </Text>
          </View>
        );

      case 'image':
        return (
          <View style={[commonStyle, {
            backgroundColor: theme ? '#f0f0f0' : '#333333',
            borderRadius: element.properties.borderRadius || 8,
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 2,
            borderColor: theme ? '#e0e0e0' : '#555555',
            borderStyle: 'dashed'
          }]}>
            <MaterialIcons
              name="image"
              size={Math.min(element.width * 0.4, element.height * 0.4, 40)}
              color={theme ? '#cccccc' : '#666666'}
            />
            <Text style={{
              fontSize: 10,
              color: theme ? '#999999' : '#666666',
              marginTop: 4
            }}>
              Image
            </Text>
          </View>
        );

      case 'container':
        return (
          <View style={[commonStyle, {
            backgroundColor: element.properties.backgroundColor || (theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'),
            borderWidth: element.properties.borderWidth || 1,
            borderColor: element.properties.borderColor || (theme ? '#e0e0e0' : '#444444'),
            borderRadius: element.properties.borderRadius || 8,
            padding: element.properties.padding || 16,
            alignItems: 'center',
            justifyContent: 'center'
          }]}>
            <MaterialIcons
              name="crop-landscape"
              size={20}
              color={theme ? '#cccccc' : '#666666'}
            />
            <Text style={{
              fontSize: 12,
              color: theme ? '#999999' : '#666666',
              marginTop: 4
            }}>
              Container
            </Text>
          </View>
        );

      case 'divider':
        return (
          <View style={[commonStyle, {
            justifyContent: 'center'
          }]}>
            <View style={{
              width: '100%',
              height: element.properties.height || 2,
              backgroundColor: element.properties.backgroundColor || (theme ? '#e0e0e0' : '#444444'),
              borderRadius: 1
            }} />
          </View>
        );

      default:
        return (
          <View style={[commonStyle, {
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            borderRadius: 4
          }]}>
            <Text style={{
              color: theme ? '#999999' : '#666666',
              fontSize: 12
            }}>
              Unknown Element
            </Text>
          </View>
        );
    }
  };

  return (
    <Animatable.View
      animation={isDragging ? 'pulse' : undefined}
      duration={100}
      style={{
        position: 'absolute',
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height
      }}
    >
      {/* Element Content */}
      <View
        {...panResponder.panHandlers}
        style={{
          width: '100%',
          height: '100%',
          borderWidth: isSelected ? 2 : isDragging ? 1 : 0,
          borderColor: isSelected ? '#2196F3' : '#2196F3',
          borderStyle: isSelected ? 'solid' : 'dashed',
          borderRadius: 4,
          backgroundColor: isDragging ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
          shadowColor: isSelected ? '#2196F3' : '#000',
          shadowOffset: { width: 0, height: isSelected ? 4 : 2 },
          shadowOpacity: isSelected ? 0.3 : isDragging ? 0.2 : 0,
          shadowRadius: isSelected ? 6 : 4,
          elevation: isSelected ? 6 : isDragging ? 4 : 0
        }}
      >
        {renderElementContent()}
      </View>

      {/* Selection handles and controls */}
      {isSelected && (
        <>
          {/* Delete button */}
          <TouchableOpacity
            onPress={onDelete}
            style={{
              position: 'absolute',
              top: -12,
              right: -12,
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: '#f44336',
              alignItems: 'center',
              justifyContent: 'center',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.3,
              shadowRadius: 4,
              elevation: 5,
              zIndex: 1000
            }}
          >
            <MaterialIcons name="close" size={16} color="#ffffff" />
          </TouchableOpacity>

          {/* Element type indicator */}
          <View
            style={{
              position: 'absolute',
              top: -8,
              left: -8,
              paddingHorizontal: 6,
              paddingVertical: 2,
              backgroundColor: '#2196F3',
              borderRadius: 4,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.3,
              shadowRadius: 4,
              elevation: 5
            }}
          >
            <Text style={{
              fontSize: 10,
              fontWeight: 'bold',
              color: '#ffffff'
            }}>
              {element.type.toUpperCase()}
            </Text>
          </View>

          {/* Resize handle */}
          <TouchableOpacity
            style={{
              position: 'absolute',
              bottom: -6,
              right: -6,
              width: 16,
              height: 16,
              backgroundColor: '#2196F3',
              borderRadius: 8,
              borderWidth: 2,
              borderColor: '#ffffff',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.3,
              shadowRadius: 4,
              elevation: 5,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MaterialIcons name="drag-indicator" size={8} color="#ffffff" />
          </TouchableOpacity>

          {/* Dimension display */}
          <View
            style={{
              position: 'absolute',
              bottom: -24,
              left: 0,
              paddingHorizontal: 4,
              paddingVertical: 1,
              backgroundColor: 'rgba(33, 150, 243, 0.9)',
              borderRadius: 3
            }}
          >
            <Text style={{
              fontSize: 9,
              color: '#ffffff',
              fontWeight: '600'
            }}>
              {Math.round(element.width)} × {Math.round(element.height)}
            </Text>
          </View>
        </>
      )}

      {/* Hover effect for non-selected elements */}
      {!isSelected && !isDragging && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderWidth: 1,
            borderColor: 'transparent',
            borderRadius: 4,
            pointerEvents: 'none'
          }}
        />
      )}
    </Animatable.View>
  );
};

export default ComponentDesigner;